import React, { useId, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { Checkbox, CircularProgress } from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { useQuery } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { fetchListImageVersions } from 'redux/slices/containerApp/apis';
const icon = <CheckBoxOutlineBlankIcon fontSize='small' />;
const checkedIcon = <CheckBoxIcon fontSize='small' />;

const FIELD_NAME = 'images';
const KEY = 'Id';
const LABEL = 'ImageFullName';

const ImageVersionSelect = (props) => {
  const {
    error,
    helperText,
    value = null,
    required,
    handleInputChange,
    excludes = [],
    textFieldProps,
    isMultiple = false,
    label,
    appInstanceId,
    containerId,
    queryKey,
    ...rest
  } = props;
  const [isEnabled, setIsEnabled] = useState(false);

  const { data, isLoading, isRefetching } = useQuery({
    enabled: isEnabled,
    queryKey: ['select-images', queryKey, appInstanceId, containerId],
    queryFn: () =>
      fetchListImageVersions({ containerAppId: containerId, appInstanceId }),
  });

  const id = useId();

  const onOpen = () => {
    setIsEnabled(true);
  };

  return (
    <Autocomplete
      fullWidth
      multiple={isMultiple}
      disableCloseOnSelect={isMultiple}
      value={value}
      id={`${FIELD_NAME}-select-${id}`}
      name={FIELD_NAME}
      options={
        data?.data?.filter((e) => !excludes?.some((f) => f[KEY] === e[KEY])) ??
        []
      }
      onOpen={onOpen}
      getOptionLabel={(option) => option[LABEL]}
      isOptionEqualToValue={(option, value) => {
        return option?.[KEY] === value?.[KEY];
      }}
      renderOption={(props, option, { selected }) => {
        const { key, ...optionProps } = props;
        return (
          <li {...optionProps} key={option?.[KEY]}>
            {isMultiple && (
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                style={{ marginRight: 8 }}
                checked={selected}
              />
            )}
            {option[LABEL]}
          </li>
        );
      }}
      loading={isLoading || isRefetching}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          error={error}
          helperText={helperText}
          placeholder={isMultiple ? 'Select Images' : 'Select Image'}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {isLoading ? (
                  <CircularProgress color='inherit' size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
          {...textFieldProps}
        />
      )}
      {...rest}
    />
  );
};

export default React.memo(ImageVersionSelect);

ImageVersionSelect.propTypes = {
  error: PropTypes.bool,
  helperText: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  required: PropTypes.bool,
  handleInputChange: PropTypes.func,
  excludes: PropTypes.array,
  textFieldProps: PropTypes.object,
  isMultiple: PropTypes.bool,
  label: PropTypes.string,
  queryKey: PropTypes.string,
  appInstanceId: PropTypes.string.isRequired,
  containerId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
};
